import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { UserPlus, Search, Edit2, ToggleLeft, ToggleRight, X, Trash2 } from 'lucide-react';
import './AdminUsers.css';

const ROLES = ['reader', 'editor', 'producer', 'admin'];
const ROLE_COLOR = { reader: '#34D399', editor: '#A78BFA', producer: '#EC4899', admin: '#F59E0B' };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'reader', roles: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try { setLoading(true); const res = await API.get('/users'); setUsers(res.data.users || res.data.data || []); }
    catch { toast.error('Gagal memuat pengguna'); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditUser(null); setForm({ name: '', email: '', password: '', role: 'reader', roles: [] }); setShowModal(true); };
  const openEdit = (u) => { setEditUser(u); setForm({ name: u.name, email: u.email, password: '', role: u.role, roles: (u.roles && u.roles.length) ? u.roles : [u.role] }); setShowModal(true); };

  const toggleRole = (r) => setForm(f => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter(x => x !== r) : [...f.roles, r] }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Nama dan email wajib diisi'); return; }
    if (!editUser && !form.password.trim()) { toast.error('Password wajib untuk user baru'); return; }
    try {
      setSaving(true);
      if (editUser) {
        await API.patch(`/users/${editUser._id}`, { name: form.name, role: form.role, roles: form.roles, ...(form.password ? { password: form.password } : {}) });
        toast.success('Pengguna diperbarui');
      } else {
        await API.post('/users', form);
        toast.success('Pengguna dibuat');
      }
      setShowModal(false); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try {
      await API.patch(`/users/${u._id}`, { isActive: !u.isActive });
      toast.success(u.isActive ? 'Akun dinonaktifkan' : 'Akun diaktifkan');
      fetchUsers();
    } catch { toast.error('Gagal mengubah status'); }
  };

  const handleDelete = async (u) => {
    try {
      await API.delete(`/users/${u._id}`);
      toast.success('Pengguna berhasil dihapus');
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal menghapus pengguna'); }
  };

  const filtered = users.filter(u => {
    const matchSearch = u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const ownedRoles = (u.roles && u.roles.length) ? u.roles : [u.role];
    const matchRole = roleFilter === 'all' || ownedRoles.includes(roleFilter);
    return matchSearch && matchRole;
  });

  return (
    <DashboardLayout title="Kelola Pengguna" subtitle="Buat dan kelola akun pengguna platform">
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--ki-border)', borderRadius: 8, padding: '9px 12px' }}>
          <Search size={14} style={{ color: 'var(--ki-text-faint)', flexShrink: 0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama atau email..." style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--ki-text)', fontSize: 13, width: '100%' }} />
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="form-select" style={{ width: 'auto', padding: '9px 14px', fontSize: 13 }}>
          <option value="all">Semua Role</option>
          {ROLES.map(r => <option key={r} value={r} style={{ textTransform: 'capitalize' }}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={openCreate}><UserPlus size={14} /> Tambah Pengguna</button>
      </div>

      {loading ? (
        <div className="ki-card">{[...Array(5)].map((_, i) => <div key={i} className="shimmer" style={{ height: 52, borderRadius: 8, marginBottom: 10 }} />)}</div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead><tr><th>Nama</th><th>Email</th><th>Role</th><th>Status</th><th>Aksi</th></tr></thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u._id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {u.avatar ? <img src={u.avatar} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                        : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,var(--in-blue),var(--in-blue-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white' }}>{u.name[0]}</div>
                      }
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-muted)' }}>{u.email}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {((u.roles && u.roles.length) ? u.roles : [u.role]).map(r => (
                        <span key={r} style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(0,21,64,0.06)', color: ROLE_COLOR[r] || 'var(--ki-text)', textTransform: 'capitalize', border: r === u.role ? '1px solid currentColor' : '1px solid transparent' }} title={r === u.role ? 'Role aktif' : 'Role tambahan'}>{r}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: u.isActive ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)', color: u.isActive ? '#34D399' : '#F87171', border: `1px solid ${u.isActive ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.3)'}` }}>
                      {u.isActive ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)} title="Edit pengguna"><Edit2 size={13} /></button>
                      <button className="btn btn-secondary btn-sm" onClick={() => toggleActive(u)} title={u.isActive ? 'Nonaktifkan' : 'Aktifkan'}>
                        {u.isActive ? <ToggleLeft size={13} /> : <ToggleRight size={13} />}
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => setDeleteTarget(u)}
                        title="Hapus pengguna"
                        style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', color: '#DC2626' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {filtered.length === 0 && <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--ki-text-muted)', fontSize: 14 }}>Tidak ada pengguna ditemukan.</div>}
        </div>
      )}

      {/* Modal Konfirmasi Hapus */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="modal-box" style={{ maxWidth: 420 }}>
            <div className="modal-head">
              <div style={{ fontSize: 16, fontWeight: 700 }}>Hapus Pengguna</div>
              <button className="btn btn-icon btn-ghost" onClick={() => setDeleteTarget(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: 'var(--ki-text-muted)', marginBottom: 14 }}>
                Anda akan menghapus pengguna berikut secara permanen:
              </p>
              <div style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{deleteTarget.name}</div>
                <div style={{ fontSize: 13, color: 'var(--ki-text-muted)', marginTop: 2 }}>{deleteTarget.email} · <span style={{ textTransform: 'capitalize' }}>{deleteTarget.role}</span></div>
              </div>
              <p style={{ fontSize: 13, color: '#DC2626', marginBottom: 0 }}>
                Tindakan ini tidak dapat dibatalkan. Data pengguna akan hilang selamanya.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Batal</button>
                <button
                  className="btn btn-sm"
                  onClick={() => handleDelete(deleteTarget)}
                  style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 18px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}
                >
                  Hapus Permanen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="modal-head">
              <div style={{ fontSize: 17, fontWeight: 700 }}>{editUser ? 'Edit Pengguna' : 'Tambah Pengguna Baru'}</div>
              <button className="btn btn-icon btn-ghost" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Nama Lengkap *</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="John Doe" />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@domain.com" disabled={!!editUser} />
              </div>
              <div className="form-group">
                <label className="form-label">{editUser ? 'Password Baru (kosongkan jika tidak diubah)' : 'Password *'}</label>
                <input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Minimal 6 karakter" />
              </div>
              {!editUser ? (
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value, roles: [e.target.value] })}>
                    {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                  </select>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Role yang Dimiliki <span style={{ fontWeight: 400, color: 'var(--ki-text-faint)' }}>(boleh lebih dari satu)</span></label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {ROLES.map(r => {
                        const active = form.roles.includes(r);
                        return (
                          <button key={r} type="button" onClick={() => toggleRole(r)}
                            style={{ padding: '6px 14px', borderRadius: 100, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                              background: active ? 'var(--in-blue)' : 'var(--in-bg-3)', color: active ? '#fff' : 'var(--ki-text-muted)',
                              border: '1px solid ' + (active ? 'var(--in-blue)' : 'var(--ki-border)') }}>
                            {r.charAt(0).toUpperCase() + r.slice(1)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {form.roles.length > 1 && (
                    <div className="form-group">
                      <label className="form-label">Role Aktif (default saat login)</label>
                      <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                        {form.roles.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                      </select>
                    </div>
                  )}
                </>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
                <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Menyimpan...' : editUser ? 'Perbarui' : 'Buat Pengguna'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
